import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { loginUser, registerUser, getMe, verifyEmail as verifyEmailApi, logoutUser } from "@/lib/api";
import i18n from "@/lib/i18n";
import { TERMS_VERSION } from "@/lib/legal";

const AuthContext = createContext(null);

// DEV: Auto-login credentials (disabled — use login page)
const AUTO_LOGIN = false;
const AUTO_LOGIN_EMAIL = "";
const AUTO_LOGIN_PASSWORD = "";

// DEV-ONLY: lokalny podglad UI bez backendu (auto-"login" mockiem, ZERO API calls).
// Aktywne TYLKO gdy: dev build (NODE_ENV=development) ORAZ serwowane z localhost.
// NIGDY na produkcji:
//   - prod build ma NODE_ENV="production" => false (build-time, nie da sie obejsc),
//   - prod domena (leslex.pl) ma hostname != localhost => false.
// Domyslnie ON przy `yarn start`. Opt-out (gdy masz lokalny backend i chcesz realny
// login): ustaw REACT_APP_DEV_MOCK_AUTH=false w frontend/.env.local.
const DEV_MOCK_AUTH =
  process.env.NODE_ENV === "development" &&
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
  process.env.REACT_APP_DEV_MOCK_AUTH !== "false";
const DEV_MOCK_USER = {
  id: "dev-mock",
  email: "demo@les-lex.local",
  name: "Demo (lokalny podgląd)",
  role: "owner",
  plan: "kancelaria_pro",
  account_status: "active",
  email_verified: true,
  language: "pl",
};

// Sync i18n language when user data changes
function syncLanguage(userData) {
  if (userData?.language) {
    const lang = userData.language;
    localStorage.setItem("language", lang);
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    if (DEV_MOCK_AUTH) return DEV_MOCK_USER;
    try {
      const stored = localStorage.getItem("les_lex_user");
      const parsed = stored ? JSON.parse(stored) : null;
      // Sync language on initial load from localStorage
      if (parsed?.language) syncLanguage(parsed);
      return parsed;
    } catch {
      // Corrupted localStorage — clear and start fresh
      localStorage.removeItem("les_lex_user");
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (DEV_MOCK_AUTH) {
      // Lokalny podglad UI — pomijamy cala logike tokena/getMe (brak backendu).
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("les_lex_token");
    if (token) {
      getMe()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem("les_lex_user", JSON.stringify(res.data));
          syncLanguage(res.data);
        })
        .catch(() => {
          localStorage.removeItem("les_lex_token");
          localStorage.removeItem("les_lex_refresh_token");
          localStorage.removeItem("les_lex_user");
          // If auto-login enabled, try to re-authenticate
          if (AUTO_LOGIN) {
            doAutoLogin();
            return;
          }
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else if (AUTO_LOGIN) {
      doAutoLogin();
    } else {
      setLoading(false);
    }

    function doAutoLogin() {
      loginUser({ email: AUTO_LOGIN_EMAIL, password: AUTO_LOGIN_PASSWORD, totp_code: "" })
        .then((res) => {
          const { access_token, user: userData } = res.data;
          localStorage.setItem("les_lex_token", access_token);
          localStorage.setItem("les_lex_user", JSON.stringify(userData));
          setUser(userData);
        })
        .catch((err) => {
          // Auto-login failed — clear stale token
          setUser(null);
        })
        .finally(() => setLoading(false));
    }
  }, []);

  const login = useCallback(async (email, password, totpCode = "") => {
    const res = await loginUser({ email, password, totp_code: totpCode });
    const { access_token, refresh_token, user: userData, requires_2fa } = res.data;

    if (requires_2fa) {
      return { requires_2fa: true, user: userData };
    }

    localStorage.setItem("les_lex_token", access_token);
    if (refresh_token) localStorage.setItem("les_lex_refresh_token", refresh_token);
    localStorage.setItem("les_lex_user", JSON.stringify(userData));
    setUser(userData);
    syncLanguage(userData);
    return userData;
  }, []);

  const register = useCallback(async (email, password, name, captchaToken = "", inviteToken = "", acceptTerms = false, termsVersion = TERMS_VERSION) => {
    const data = { email, password, name };
    if (captchaToken) data.captcha_token = captchaToken;
    if (inviteToken) data.invite_token = inviteToken;
    // K7 (10.09.2026): checkbox zgody byl dotad TYLKO po stronie klienta i nigdy nie
    // docieral do backendu — w bazie nie bylo sladu akceptacji Regulaminu.
    data.terms_accepted = Boolean(acceptTerms);
    data.terms_version = termsVersion;
    const res = await registerUser(data);
    const { requires_verification, email: regEmail } = res.data;
    if (requires_verification) {
      // Don't save tokens — user must verify email first
      return { requiresVerification: true, email: regEmail || email };
    }
    // Fallback for backward compat (shouldn't happen with new backend)
    const { access_token, refresh_token, user: userData } = res.data;
    if (access_token) {
      localStorage.setItem("les_lex_token", access_token);
      if (refresh_token) localStorage.setItem("les_lex_refresh_token", refresh_token);
      localStorage.setItem("les_lex_user", JSON.stringify(userData));
      setUser(userData);
    }
    return res.data;
  }, []);

  const verifyEmailToken = useCallback(async (token) => {
    const res = await verifyEmailApi(token);
    const { access_token, refresh_token, user: userData } = res.data;
    localStorage.setItem("les_lex_token", access_token);
    if (refresh_token) localStorage.setItem("les_lex_refresh_token", refresh_token);
    localStorage.setItem("les_lex_user", JSON.stringify(userData));
    setUser(userData);
    syncLanguage(userData);
    return userData;
  }, []);

  const loginWithToken = useCallback((token, userData, refreshToken) => {
    localStorage.setItem("les_lex_token", token);
    if (refreshToken) localStorage.setItem("les_lex_refresh_token", refreshToken);
    localStorage.setItem("les_lex_user", JSON.stringify(userData));
    setUser(userData);
    syncLanguage(userData);
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem("les_lex_refresh_token");
    if (refreshToken) {
      try {
        await logoutUser(refreshToken);
      } catch {}
    }
    localStorage.removeItem("les_lex_token");
    localStorage.removeItem("les_lex_refresh_token");
    localStorage.removeItem("les_lex_user");
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      localStorage.setItem("les_lex_user", JSON.stringify(res.data));
      syncLanguage(res.data);
    } catch {}
  }, []);

  const isSuspended = user?.account_status === "suspended";

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, register, verifyEmailToken, logout, refreshUser, isSuspended }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
